/**
 * Fällige Prüfungen — die Ansicht, mit der die Woche geplant wird.
 *
 * Überfällige zuerst, dann die nächstfälligen. Sie muss auch bei tausenden
 * Spielplätzen sofort da sein; sortiert, gefiltert und begrenzt wird
 * deshalb in der Datenbank, und die Oberfläche holt eine Seite nach der
 * anderen.
 *
 * Zwei Entscheidungen zur Darstellung:
 *
 * **Überfällig wird nicht farbig markiert.** Farbe ist in dieser Anwendung
 * für Mängelstufen reserviert — Rot heißt „gefährlich, Gerät sperren" und
 * darf nicht auch „Termin verpasst" heißen. Überfälliges steht deshalb
 * fett, mit Zeichen und mit ausgeschriebener Angabe: „14 Tage überfällig".
 *
 * **Der Filterstand steht in der Adresse.** Ein Lesezeichen auf „überfällig
 * in Emden" funktioniert, und wer in einen Prüfbogen springt und
 * zurückkommt, findet dieselbe Liste vor.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useKunden, usePruefungAnlegen } from '../api/abfragen'
import {
  useAufgabeAendern,
  useErinnerungenVersenden,
  useKalenderabo,
} from '../api/aufgaben'
import {
  type Faelligkeitsfilter,
  type Faelligkeitszeile,
  type Zeitraum,
  useFaellige,
} from '../api/faellig'
import { PRUEFARTTEXT, type Pruefart } from '../api/typen'
import { useBenutzer } from '../api/verwaltung'
import { useNavigate } from 'react-router-dom'
import { Datumwahl } from '../bausteine/Datumwahl'
import { useEntprellt } from '../bausteine/Entprellt'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis, datumDeutsch } from '../bausteine/Hinweise'
import { darfPruefen, darfVerwalten, useKonto } from '../konto/KontoKontext'

const ZEITRAEUME: { wert: Zeitraum; text: string }[] = [
  { wert: 'ueberfaellig', text: 'Überfällig' },
  { wert: 'woche', text: 'Diese Woche' },
  { wert: 'monat', text: 'Dieser Monat' },
  { wert: 'frei', text: 'Zeitraum wählen' },
]

const ARTEN: Pruefart[] = ['hauptuntersuchung', 'operativ', 'nachpruefung', 'abnahme']

export function Faellig() {
  const [adresse, setzeAdresse] = useSearchParams()
  const { konto } = useKonto()
  const kunden = useKunden()
  // Die Prüferliste gibt es nur für die Verwaltung; sonst bleibt der
  // Filter weg statt eine leere Auswahl anzubieten.
  const benutzer = useBenutzer({ enabled: darfVerwalten(konto) })

  /* Was in der Adresse steht, ist der Zustand. Ein zweiter Zustand daneben
     liefe auseinander, sobald jemand zurückspringt.

     Gelesen wird dabei die lebende Adresse und nicht `adresse` aus dem
     Rendern — auch nicht über die Aktualisierungsfunktion von
     `setSearchParams`. Das ist nicht Vorsicht, sondern nachgemessen:
     `setSearchParams` ist ein `useCallback` mit `searchParams` in den
     Abhängigkeiten und reicht der Aktualisierungsfunktion den Stand aus
     genau diesem Closure. React Router führt Navigationen als Übergang
     aus; die Adresse ist also schon geändert, während der neue Closure
     noch nicht steht. Zwei Änderungen kurz hintereinander löschten
     einander deshalb aus — „Überfällig" verschwand, sobald man einen Ort
     eintippte. */
  const setzen = (name: string, wert: string) => {
    const neu = new URLSearchParams(window.location.search)
    if (wert) neu.set(name, wert)
    else neu.delete(name)
    setzeAdresse(neu, { replace: true })
  }

  // Das Suchfeld tippt sich sofort, fragt aber verzögert.
  const [suchtext, setzeSuchtext] = useState(adresse.get('suche') ?? '')
  const entprellt = useEntprellt(suchtext, 300)
  useEffect(() => {
    if ((adresse.get('suche') ?? '') !== entprellt) setzen('suche', entprellt)
    // Nur auf den entprellten Wert hören: sonst schriebe jeder Tastendruck
    // in die Adresse und der Verlauf wäre unbrauchbar.
     
  }, [entprellt])

  const zeitraum = (adresse.get('zeitraum') ?? '') as Zeitraum | ''
  const filter: Faelligkeitsfilter = {
    zeitraum: zeitraum || undefined,
    von: adresse.get('von') || undefined,
    bis: adresse.get('bis') || undefined,
    kunde_id: adresse.get('kunde_id') || undefined,
    pruefart: (adresse.get('pruefart') as Pruefart) || undefined,
    ort: adresse.get('ort') || undefined,
    pruefer_id: adresse.get('pruefer_id') || undefined,
    suche: adresse.get('suche') || undefined,
    alle_zustaende: adresse.get('alle_zustaende') === '1' || undefined,
  }

  const seiten = useFaellige(filter)
  const erinnerungen = useErinnerungenVersenden()
  const abo = useKalenderabo()
  const aendern = useAufgabeAendern()
  const starten = usePruefungAnlegen()
  const navigiere = useNavigate()
  const [verschieben, setzeVerschieben] = useState<Faelligkeitszeile | null>(null)
  const erste = seiten.data?.pages[0]
  const zeilen = (seiten.data?.pages ?? []).flatMap((s) => s.zeilen)
  const gefiltert = Object.values(filter).some((w) => w !== undefined)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-2xl font-bold">Fällige Prüfungen</h1>
        {erste && (
          <span aria-live="polite" className="text-base text-slate-700">
            {erste.mehr_als
              ? `mehr als ${erste.anzahl} Treffer — bitte filtern`
              : `${erste.anzahl} ${erste.anzahl === 1 ? 'Treffer' : 'Treffer'}`}
          </span>
        )}
        {gefiltert && (
          <button
            type="button"
            className="knopf-still px-3 text-base"
            onClick={() => setzeAdresse(new URLSearchParams(), { replace: true })}
          >
            Filter zurücksetzen
          </button>
        )}
        <span className="ml-auto flex flex-wrap gap-2">
          {darfPruefen(konto) && (
            <button
              type="button"
              className="knopf-still px-3 text-base"
              disabled={erinnerungen.isPending}
              onClick={() => erinnerungen.mutate()}
            >
              {erinnerungen.isPending ? 'Sendet …' : 'Erinnerungen jetzt senden'}
            </button>
          )}
          <button
            type="button"
            className="knopf-zweit px-3 text-base"
            disabled={abo.isPending}
            onClick={() => abo.mutate()}
          >
            Kalenderabo
          </button>
        </span>
      </div>

      {abo.data && (
        <div className="karte space-y-2">
          <h2 className="font-bold">Kalenderabo</h2>
          <p className="text-base text-slate-700">{abo.data.hinweis}</p>
          <input readOnly value={abo.data.adresse} onFocus={(e) => e.target.select()} />
        </div>
      )}
      <Fehlerhinweis fehler={erinnerungen.error ?? abo.error} />
      {erinnerungen.data && (
        <p role="status" className="text-base text-slate-700">
          {erinnerungen.data.verschickt} Erinnerungen verschickt.
        </p>
      )}

      <div className="karte space-y-3">
        <div className="flex flex-wrap gap-2">
          {ZEITRAEUME.map((z) => (
            <button
              key={z.wert}
              type="button"
              aria-pressed={zeitraum === z.wert}
              className={`knopf-still px-3 text-base ${
                zeitraum === z.wert ? 'border-akzent-500 bg-akzent-grund' : ''
              }`}
              onClick={() => setzen('zeitraum', zeitraum === z.wert ? '' : z.wert)}
            >
              {z.text}
            </button>
          ))}
        </div>

        {zeitraum === 'frei' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="f-von">Von</label>
              <input
                id="f-von"
                type="date"
                value={adresse.get('von') ?? ''}
                onChange={(e) => setzen('von', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="f-bis">Bis</label>
              <input
                id="f-bis"
                type="date"
                value={adresse.get('bis') ?? ''}
                onChange={(e) => setzen('bis', e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="f-suche">Kunde oder Spielplatz</label>
            <input
              id="f-suche"
              value={suchtext}
              placeholder="Krummhörn, Am Deich …"
              onChange={(e) => setzeSuchtext(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="f-ort">Ort oder PLZ</label>
            <input
              id="f-ort"
              value={adresse.get('ort') ?? ''}
              placeholder="Emden oder 267"
              onChange={(e) => setzen('ort', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="f-kunde">Kunde</label>
            <select
              id="f-kunde"
              value={adresse.get('kunde_id') ?? ''}
              onChange={(e) => setzen('kunde_id', e.target.value)}
            >
              <option value="">alle</option>
              {(kunden.data ?? []).map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="f-art">Prüfart</label>
            <select
              id="f-art"
              value={adresse.get('pruefart') ?? ''}
              onChange={(e) => setzen('pruefart', e.target.value)}
            >
              <option value="">alle</option>
              {ARTEN.map((a) => (
                <option key={a} value={a}>
                  {PRUEFARTTEXT[a]}
                </option>
              ))}
            </select>
          </div>
          {darfVerwalten(konto) && (
            <div>
              <label htmlFor="f-pruefer">Zuständig</label>
              <select
                id="f-pruefer"
                value={adresse.get('pruefer_id') ?? ''}
                onChange={(e) => setzen('pruefer_id', e.target.value)}
              >
                <option value="">alle</option>
                {(benutzer.data ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <label className="flex items-end gap-2 pb-2">
            <input
              type="checkbox"
              className="h-5 w-5"
              checked={adresse.get('alle_zustaende') === '1'}
              onChange={(e) => setzen('alle_zustaende', e.target.checked ? '1' : '')}
            />
            <span className="text-base font-normal">
              Erledigte und entfallene mitzeigen
            </span>
          </label>
        </div>
      </div>

      <Fehlerhinweis fehler={seiten.error} />

      {seiten.isLoading ? (
        <Ladeanzeige />
      ) : zeilen.length === 0 ? (
        <Leerhinweis
          titel="Nichts fällig"
          text={
            gefiltert
              ? 'Mit diesen Filtern steht nichts an.'
              : 'Es stehen keine Prüfungen an.'
          }
        />
      ) : (
        <>
          <ul className="space-y-2">
            {zeilen.map((z) => (
              <Zeile
                key={z.id}
                zeile={z}
                beimVerschieben={() => setzeVerschieben(z)}
                beimErledigen={() =>
                  aendern.mutate({ id: z.id, daten: { status: 'erledigt' } as never })
                }
                beimBeginnen={() =>
                  starten.mutate(
                    {
                      spielplatz_id: z.spielplatz_id,
                      pruefart: z.pruefart,
                      datum: erste?.heute ?? new Date().toISOString().slice(0, 10),
                    },
                    {
                      onSuccess: (pruefung) => {
                        aendern.mutate({
                          id: z.id,
                          daten: { status: 'eingeplant' } as never,
                        })
                        navigiere(`/pruefungen/${pruefung.id}/bogen`)
                      },
                    },
                  )
                }
              />
            ))}
          </ul>
          <Nachladen
            laden={() => void seiten.fetchNextPage()}
            kannNachladen={seiten.hasNextPage}
            laeuft={seiten.isFetchingNextPage}
          />
        </>
      )}

      <Fehlerhinweis fehler={aendern.error ?? starten.error} />

      {verschieben && (
        <Datumwahl
          titel="Fälligkeit verschieben"
          untertitel={`${verschieben.spielplatz_name} · ${PRUEFARTTEXT[verschieben.pruefart]}`}
          wert={verschieben.faellig_am}
          hinweis={`Bisher fällig am ${datumDeutsch(verschieben.faellig_am)}.`}
          laeuft={aendern.isPending}
          fehler={aendern.error}
          schliessen={() => {
            setzeVerschieben(null)
            aendern.reset()
          }}
          beimWaehlen={(iso) =>
            aendern.mutate(
              { id: verschieben.id, daten: { faellig_am: iso } as never },
              { onSuccess: () => setzeVerschieben(null) },
            )
          }
        />
      )}
    </div>
  )
}

function Zeile({
  zeile: z,
  beimVerschieben,
  beimErledigen,
  beimBeginnen,
}: {
  zeile: Faelligkeitszeile
  beimVerschieben: () => void
  beimErledigen: () => void
  beimBeginnen: () => void
}) {
  const ueberfaellig = z.tage_bis_faellig < 0
  const tage = Math.abs(z.tage_bis_faellig)
  const [offen, setzeOffen] = useState(false)
  const { konto } = useKonto()

  return (
    <li>
      {/* Der ganze Eintrag ist das Tippziel und führt in den Prüfbogen des
          Platzes — auf dem Handy ist jeder Zwischenschritt einer zu viel. */}
      <Link
        to={`/spielplaetze/${z.spielplatz_id}`}
        className="karte-knopf items-start"
        style={{ minHeight: '48px' }}
      >
        <span className="min-w-0 flex-1">
          <span className="block font-semibold">
            {z.kunde_name} · {z.spielplatz_name}
          </span>
          <span className="block text-base text-slate-700">
            {[z.plz, z.ort].filter(Boolean).join(' ')}
            {z.ort ? ' · ' : ''}
            {PRUEFARTTEXT[z.pruefart]}
            {z.zugewiesen_an_name ? ` · ${z.zugewiesen_an_name}` : ''}
          </span>
          <span className="block text-base text-slate-600">
            Zuletzt geprüft:{' '}
            {z.letzte_pruefung_am ? datumDeutsch(z.letzte_pruefung_am) : 'noch nie'}
          </span>
        </span>

        {/* Keine Farbe: die ist für Mängelstufen reserviert. Fettung,
            Zeichen und ausgeschriebene Angabe tragen dasselbe. */}
        <span className="shrink-0 text-right">
          <span
            className={
              ueberfaellig ? 'block font-bold' : 'block text-base text-slate-700'
            }
          >
            {ueberfaellig && <span aria-hidden>⚠ </span>}
            {datumDeutsch(z.faellig_am)}
          </span>
          <span className={`block text-base ${ueberfaellig ? 'font-bold' : 'text-slate-600'}`}>
            {ueberfaellig
              ? `${tage} ${tage === 1 ? 'Tag' : 'Tage'} überfällig`
              : z.tage_bis_faellig === 0
                ? 'heute fällig'
                : `in ${tage} ${tage === 1 ? 'Tag' : 'Tagen'}`}
          </span>
        </span>
      </Link>

      {/* Ein Tipp bis in den Prüfbogen. Sichtbar und nicht hinter einem
          Menü: das ist der Griff, mit dem die Arbeit anfängt.

          Nicht die ganze Zeile, sondern ein eigener Knopf — die Zeile
          führt auf den Platz, und ein versehentlicher Tipp soll keine
          Prüfung anlegen. Ist für den Platz schon eine offene Prüfung
          derselben Art da, führt derselbe Knopf in sie hinein statt eine
          zweite anzulegen; das entscheidet der Server. */}
      <div className="mt-1 flex flex-wrap gap-2">
        {darfPruefen(konto) && (
          <button
            type="button"
            className="knopf-zweit px-3 text-base"
            style={{ minHeight: '48px' }}
            onClick={beimBeginnen}
          >
            Prüfung beginnen
          </button>
        )}
        <button
          type="button"
          className="knopf-still px-3 text-base"
          style={{ minHeight: '48px' }}
          aria-expanded={offen}
          onClick={() => setzeOffen((o) => !o)}
        >
          {offen ? 'Weniger' : 'Mehr'}
        </button>
        {offen && (
          <>
            <button type="button" className="knopf-still px-3 text-base" onClick={beimVerschieben}>
              Verschieben
            </button>
            <button type="button" className="knopf-still px-3 text-base" onClick={beimErledigen}>
              Als erledigt eintragen
            </button>
          </>
        )}
      </div>
    </li>
  )
}

/** Lädt nach, sobald der Fuß der Liste ins Bild kommt. */
function Nachladen({
  laden,
  kannNachladen,
  laeuft,
}: {
  laden: () => void
  kannNachladen: boolean
  laeuft: boolean
}) {
  const marke = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ziel = marke.current
    if (!ziel || !kannNachladen) return
    const beobachter = new IntersectionObserver(
      (eintraege) => {
        if (eintraege[0]?.isIntersecting) laden()
      },
      // Etwas vorher anfangen, damit die Liste nicht sichtbar stockt.
      { rootMargin: '400px' },
    )
    beobachter.observe(ziel)
    return () => beobachter.disconnect()
  }, [laden, kannNachladen])

  if (!kannNachladen) return null

  return (
    <div ref={marke} className="py-4 text-center">
      {/* Der Knopf ist kein Zierrat: ohne Beobachter — etwa wenn jemand
          mit der Tastatur arbeitet — bleibt er der Weg zur nächsten Seite. */}
      <button
        type="button"
        className="knopf-still"
        disabled={laeuft}
        onClick={laden}
      >
        {laeuft ? 'Lädt …' : 'Weitere laden'}
      </button>
    </div>
  )
}
